import React, { useState } from 'react';
import { Dialog } from '@primer/react/drafts';
import {
  Box,
  Text,
  TextInput,
  FormControl,
  Button,
  ButtonGroup,
  ToggleSwitch
} from '@primer/react';
import { useForm } from 'react-hook-form';
import { useCommonContext } from '../../../../contexts/common';
import { thiSinhApi } from '../../../../api/thiSinhApi';
import { NotifyHelper } from '../../../../helpers/toast';

interface DanhSoBaoDanhModalProps {
  dm_truong_id: number;
  ex_kythi_id: number;
  nam_hoc: string;
  onCancel: () => void;
  onSuccess: () => void;
}

interface FormData {
  dm_truong_id: number;
  ex_kythi_id: number;
  nam_hoc: string;
  tien_to: string;
  dat_lai_toan_bo: boolean;
  so_bat_dau: number;
}

const DanhSoBaoDanhModal: React.FC<DanhSoBaoDanhModalProps> = ({
  dm_truong_id,
  ex_kythi_id,
  nam_hoc,
  onCancel,
  onSuccess
}) => {
  const { translate } = useCommonContext();
  const [isSaving, setIsSaving] = useState(false);
  
  const defaultValues: FormData = {
    dm_truong_id,
    ex_kythi_id,
    nam_hoc,
    tien_to: '',
    dat_lai_toan_bo: false,
    so_bat_dau: 1
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch
  } = useForm<FormData>({
    defaultValues
  });

  const formValues = watch();

  const onSubmit = async (data: FormData) => {
    setIsSaving(true);
    try {
      const res = await thiSinhApi.danhSoBaoDanh(data);
      if (res.is_success) {
        NotifyHelper.Success(translate('Success.AssignNumber'));
        onSuccess();
      } else {
        NotifyHelper.Error(res.message || translate('Error.Generic'));
      }
    } catch (error) {
      NotifyHelper.Error(translate('Error.Generic'));
    }
    setIsSaving(false);
  };

  return (
    <Dialog
      title={translate('Đánh số báo danh')}
      onClose={onCancel}
      width="medium"
    >
      <Box as="form" onSubmit={handleSubmit(onSubmit)} sx={{ p: 0 }}>
        <FormControl sx={{ mb: 3 }} required>
          <FormControl.Label>{translate('Tiền tố')}</FormControl.Label>
          <TextInput
            {...register('tien_to', {
              required: translate('Validation.Required')
            })}
            block
            aria-label={translate('Prefix')}
            validationStatus={errors.tien_to ? 'error' : undefined}
          />
          {errors.tien_to && (
            <FormControl.Validation variant="error">
              {errors.tien_to.message}
            </FormControl.Validation>
          )}
        </FormControl>

        <FormControl sx={{ mb: 3 }} required >
          <FormControl.Label>{translate('Số bắt đầu')}</FormControl.Label>
          <TextInput
            {...register('so_bat_dau', {
              required: translate('Validation.Required'),
              min: {
                value: 1,
                message: translate('Validation.MinValue')
              }
            })}
            type="number"
            block
            aria-label={translate('StartNumber')}
            validationStatus={errors.so_bat_dau ? 'error' : undefined}
          />
          {errors.so_bat_dau && (
            <FormControl.Validation variant="error">
              {errors.so_bat_dau.message}
            </FormControl.Validation>
          )}
          <FormControl.Caption>
            {translate('DefaultValue1')}
          </FormControl.Caption>
        </FormControl>

        <FormControl sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <FormControl.Label sx={{ mb: 0 }}>
              {translate('Đặt lại toàn bộ')}
            </FormControl.Label>
            <ToggleSwitch
              checked={formValues.dat_lai_toan_bo}
              onClick={(e) => {
                e.preventDefault();
                setValue('dat_lai_toan_bo', !formValues.dat_lai_toan_bo);
              }}
              aria-label={translate('ReassignNumbers')}
            />
          </Box>
        </FormControl>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
          <ButtonGroup>
            <Button onClick={onCancel}>
              {translate('Base.Label.Cancel')}
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={isSaving}
            >
              {translate('Đánh số báo danh')}
            </Button>
          </ButtonGroup>
        </Box>
      </Box>
    </Dialog>
  );
};

export default DanhSoBaoDanhModal;